import { PaymentGoalContext } from '../../../../../../payment-goal-context';
import {reachGoal} from 'utils/metrica';
import {formatReferenceCopy} from 'utils/typography';
import React from "react";

import {useTranslation} from "react-i18next";
import { useFieldArray, useFormContext } from "react-hook-form";
import isEmpty from 'lodash/isEmpty'

import { Check as CheckIcon } from '@vedmarket/ui-kit';
import Button from "@mui/material/Button";

import PaymentItem from '../payment-item';
import PaymentTabs from '../payment-tabs';
import CustomTabPanel from '../custom-tab-panel';

import {
  useUpdateBankCommissionsMutation,
  useBankCommissionsByInvoiceId,
  useSetCompletedByOrderIdMutation,
} from "../../../../../../../../../../../api/calc";

import { NOTIFICATION_TYPE} from "../../../../../../../../../../common/notification";
import Notification from "../../../../../../../../../../common/notification";
import AlertBottom from "../../../../../../../../../../common/alertBottom";

import {useGetCalcIdFromRoute} from "../../../../../../../../../../../utils/hooks";
import {ADD_PAYMENT_FORM, PaymentsType} from '../../index';
import {useDemoBankInvoice} from 'components/cabinet/demo/steps/payment/use-payment';
import {isCalculationViewModeError} from 'utils/helpers';
import {useCalculationReadOnly} from '../../../../../../../../calculation-read-only';

const NewPayments = ({ invoiceNumber, invoiceAmount, invoiceId, refetch, initialValues, onSaved }: BankCommissionNewPaymentsProps) => {

  const { t } = useTranslation();
  const { fields: goalFields } = React.useContext(PaymentGoalContext);

  const copy = (key: string) => formatReferenceCopy(t(key));
  const readOnly = useCalculationReadOnly()

  const [ openAlert, setOpenAlert ] = React.useState(false);
  const [ errorText, setErrorText ] = React.useState('');
  const [ formIsReady, setFormIsReady ] = React.useState(false);

  const [ tabValue, setTabValue ] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const excludedFields = [
    ADD_PAYMENT_FORM.MIN_BANK_TRANSFER_FEE,
    ADD_PAYMENT_FORM.MAX_BANK_TRANSFER_FEE,
    ADD_PAYMENT_FORM.MIN_CURRENCY_CONTROL_FEE,
    ADD_PAYMENT_FORM.MAX_CURRENCY_CONTROL_FEE,
  ];

  const [ saveBankCommissions ] = useUpdateBankCommissionsMutation()

  const [ setCompleted ] = useSetCompletedByOrderIdMutation()

  const orderId = useGetCalcIdFromRoute()

  const {  control, handleSubmit, getValues, watch, formState: { errors } } = useFormContext<PaymentsType>()

  const { fields, append, remove } = useFieldArray({
    control,
    // @ts-ignore
    name: 'payments',
  });

  const allFieldsFilled = (item: Record<string, any>) => {
    return Object.entries(item)
    // @ts-ignore
      .filter(([ key ]) => !excludedFields.includes(key))
      .every(([ , value ]) => value !== "");
  };

  const allItemsFilled = () => {
    const allPayments = getValues().payments;
    return allPayments.every((payment) => allFieldsFilled(payment));
  };

  const handleClickNewButton = () => {
    setTabValue(fields.length);
  }

  watch(() => {
    setFormIsReady(allItemsFilled());
  });

  const invoiceBankCommissions = useBankCommissionsByInvoiceId(invoiceNumber);

  const isCompletedInvoice = !isEmpty(invoiceBankCommissions?.banking_commissions);

  const paymentFormIsInvalid = !formIsReady || !isEmpty(errors);

  const saveButtonIsDisabled = readOnly || (!isCompletedInvoice && paymentFormIsInvalid);
  const demoPlayback = useDemoBankInvoice(invoiceId, saveButtonIsDisabled)

  const onSubmit = handleSubmit (async (values) => {
    let shouldUpdateCompleted = true;

    try {

      const response: any = isCompletedInvoice ?
        invoiceBankCommissions && await saveBankCommissions({
          invoiceId,
          payments: invoiceBankCommissions.banking_commissions
        }) :
        await saveBankCommissions({invoiceId, payments : values.payments})

      if (isCalculationViewModeError(response)) {
        shouldUpdateCompleted = false;
        return;
      }

      if (response?.error) {
        setErrorText(response.error.data.error || copy("common__text__error"));
      } else {
        setErrorText('')
        setOpenAlert(true)
        refetch()
        onSaved?.()
      }
    } catch (error) {
      console.log(error)
    } finally {
      if (!demoPlayback.isDemoMode && shouldUpdateCompleted) {
        setCompleted({
          orderId,
          banking_commissions: false,
          additional_services: false
        })
      }
    }
  })

  const handleRemovePaymentFromArray = (index: number) => {

    if (fields.length == index + 1) {
      setTabValue(fields.length - 2);
    }

    remove(index);
  }


  return (
    <>
      <div className='payments-tabs'>
        <PaymentTabs
          tabValue={tabValue}
          handleChange={handleChange}
          fields={fields}
          getValues={getValues}
          append={append}
          initialValues={initialValues}
          onClickNewButton={handleClickNewButton}
          shouldAppendItem
          shouldShowPaymentTitle={demoPlayback.shouldShowPaymentTitle}
        />
        {fields.map((field, index) => {
          const isFirstItem = index === 0;
          return (
            <CustomTabPanel value={tabValue} index={index} key={index}>
              <PaymentItem
                control={control}
                index={index}
                isFirstItem={isFirstItem}
                invoiceAmount={invoiceAmount}
                remove={fields.length == 1 ? undefined : handleRemovePaymentFromArray}
              />
            </CustomTabPanel>
          )
        })}
      </div>
      {<Button
        {...demoPlayback.invoiceProps}
        variant='contained'
        fullWidth
        startIcon={<CheckIcon />}
        disabled={demoPlayback.saveButtonDisabled}
        onClick={(event) => {
          reachGoal({
            goal: 'platezhi_po_schotu',
            params: goalFields,
          });
          onSubmit(event);
        }}
        sx={{ marginTop: 3 }}
      >{`${copy('calc__text__bankCommissions__savePayments')} №${invoiceNumber}` }</Button>}
      {errorText && <Notification
        type={NOTIFICATION_TYPE.WARNING}
        title={copy('common__message__warning')}
        text={errorText}
        sx={{ marginTop: 3 }}
        noCloseButton
      />}
      {openAlert && (
        <AlertBottom
          open={openAlert}
          onClose={() => setOpenAlert(false)}
          message={copy('common__message__dataSavedSuccessfully')}
        />
      )}
    </>
  );
}

export default NewPayments
